/**
 * DIEUKYDIEU.TV - APPLE NEWS SIDEBAR MODULE (js/sidebar.js)
 * Collapsible section accordion persistence, favorites & reading history,
 * featured stories shortcuts, and sitemap category navigation.
 */

import { state } from './state.js';
import { t } from './i18n.js';
import { showToast, closeMobileSidebar } from './ui.js';
import { getLikedList } from './social.js';
import { switchNav, navigateToView } from './router.js';
import { renderSidebarDynamicMenus, updateSidebarActive } from './modules/sidebar-render.js';
export { renderSidebarDynamicMenus, updateSidebarActive };
const STORAGE_COLLAPSED_KEY = 'dieukydieu_sidebar_collapsed_groups';
const STORAGE_HISTORY_KEY = 'dieukydieu_history';

/**
 * Get collapsed groups set from localStorage
 */
function getCollapsedGroups() {
    try {
        const stored = localStorage.getItem(STORAGE_COLLAPSED_KEY);
        if (stored) return JSON.parse(stored);
    } catch (e) {}
    return ['grp-links'];
}

function saveCollapsedGroups(groups) {
    try {
        localStorage.setItem(STORAGE_COLLAPSED_KEY, JSON.stringify(groups));
    } catch (e) {}
}

/**
 * Toggle collapsible sidebar group with chevron transition
 */
export function toggleSidebarGroup(groupId, event) {
    if (event) {
        event.stopPropagation();
        event.preventDefault();
    }
    const el = document.getElementById(groupId);
    if (!el) return;

    let collapsedList = getCollapsedGroups();
    const isCurrentlyCollapsed = el.classList.contains('collapsed');

    if (isCurrentlyCollapsed) {
        el.classList.remove('collapsed');
        collapsedList = collapsedList.filter(id => id !== groupId);
    } else {
        el.classList.add('collapsed');
        if (!collapsedList.includes(groupId)) {
            collapsedList.push(groupId);
        }
    }

    saveCollapsedGroups(collapsedList);
}

export function navigateToSidebarGroup(type) {
    if (type === 'explore') {
        const grp = document.getElementById('grp-explore');
        if (grp?.classList.contains('collapsed')) toggleSidebarGroup('grp-explore');
        if (typeof switchNav === 'function') switchNav('category-view', 1);
        if (typeof closeMobileSidebar === 'function') closeMobileSidebar();
    } else if (type === 'media') {
        const grp = document.getElementById('grp-media');
        if (grp?.classList.contains('collapsed')) toggleSidebarGroup('grp-media');
        if (typeof switchNav === 'function') switchNav('media-view', 2);
        if (typeof closeMobileSidebar === 'function') closeMobileSidebar();
    } else if (type === 'links') {
        const grp = document.getElementById('grp-links');
        if (grp?.classList.contains('collapsed')) toggleSidebarGroup('grp-links');
        if (typeof window.scrollToExploreNetwork === 'function') window.scrollToExploreNetwork();
        else if (typeof switchNav === 'function') switchNav('category-view', 1);
        updateSidebarActive('links');
        if (typeof closeMobileSidebar === 'function') closeMobileSidebar();
    }
}

/**
 * Toggle category level-2 sub-tree expansion
 */
export function toggleCategoryTree(treeId, event) {
    if (event) {
        event.stopPropagation();
        event.preventDefault();
    }
    const el = document.getElementById(treeId);
    if (!el) return;
    el.classList.toggle('expanded');
}

/**
 * Restore sidebar collapsible groups from localStorage on startup
 */
export function initSidebarAccordion() {
    renderSidebarDynamicMenus();
    const collapsedList = getCollapsedGroups();
    const allGroups = document.querySelectorAll('.desktop-sidebar .sidebar-group');

    allGroups.forEach(group => {
        const gid = group.id;
        if (gid) {
            if (collapsedList.includes(gid)) {
                group.classList.add('collapsed');
            } else {
                group.classList.remove('collapsed');
            }
        }
    });

    updateSidebarFavoritesBadge();
}

/**
 * Update real-time counter chip for "Bài viết yêu thích"
 */
export function updateSidebarFavoritesBadge() {
    const chip = document.getElementById('sidebar-fav-count');
    if (!chip) return;
    const likes = getLikedList();
    chip.innerText = likes.length;
}

/**
 * Open Favorites (Bài viết yêu thích) View
 */
export function openFavoritesView() {
    if (typeof closeMobileSidebar === 'function') {
        closeMobileSidebar();
    }
    const likes = getLikedList();
    if (likes.length === 0) {
        showToast(t('toast_no_favorites') || 'Chưa có bài viết yêu thích nào ❤️');
    }
    // Switch to Explore view and filter or highlight
    switchNav('category-view', 1);

    if (typeof window.filterExploreByFavorites === 'function') {
        window.filterExploreByFavorites();
    } else {
        showToast('Đang hiển thị danh mục bài viết');
    }
}

/**
 * Record an article into reading history
 */
export function recordReadingHistory(article) {
    if (!article || !article.id) return;
    try {
        let history = JSON.parse(localStorage.getItem(STORAGE_HISTORY_KEY) || '[]');
        history = history.filter(item => item.id !== article.id);
        history.unshift({
            id: article.id,
            title: article.title || '',
            tag: article.tag || '',
            time: Date.now()
        });
        if (history.length > 30) history = history.slice(0, 30);
        localStorage.setItem(STORAGE_HISTORY_KEY, JSON.stringify(history));
    } catch (e) {}
}

/**
 * Open History (Lịch sử đã xem) View
 */
export function openHistoryView() {
    if (typeof closeMobileSidebar === 'function') {
        closeMobileSidebar();
    }
    try {
        const history = JSON.parse(localStorage.getItem(STORAGE_HISTORY_KEY) || '[]');
        if (history.length === 0) {
            showToast('Lịch sử xem bài đang trống');
            return;
        }
        showToast(`Bạn đã xem ${history.length} bài viết gần đây`);
        switchNav('category-view', 1);
    } catch (e) {
        switchNav('category-view', 1);
    }
}

/**
 * Open "3 Bài viết bạn cần xem" (Hero feature)
 */
export function openFeaturedArticles() {
    if (typeof closeMobileSidebar === 'function') {
        closeMobileSidebar();
    }
    // Switch to Home view and smoothly scroll to the main featured showcase / carousel
    switchNav('home-view', 0);
    const showcase = document.querySelector('.carousel-container') || document.querySelector('.featured-story-hero');
    if (showcase) {
        showcase.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    showToast('3 Bài viết bạn cần xem');
}
